/**
 * Connectivity detection.
 *
 * `navigator.onLine` is necessary but nowhere near sufficient. It reports true
 * whenever a network interface is up — which includes the two cases that matter
 * most in a township shop:
 *
 *   - a captive portal that intercepts every request;
 *   - a router with no upstream, mid load-shedding.
 *
 * In both, a POS that trusted `navigator.onLine` would keep trying to take card
 * payments and keep failing. So the flag is treated as a *hint* — cheap and
 * instant — and confirmed against an authenticated reachability probe.
 *
 * The probe hits `/health/`, which is unauthenticated, dependency-free and
 * returns a few bytes. It is deliberately not an API call: a probe that could be
 * refused for an expired token would report "offline" for an authentication
 * problem, which is a different thing and needs a different message.
 */

export type Reachability = boolean | null;

export interface ConnectivityOptions {
  /** How often to probe while healthy. */
  healthyIntervalMs?: number;
  /** How often while down — more eagerly, so trading resumes promptly. */
  offlineIntervalMs?: number;
  /** Abort a probe that hangs; a captive portal will happily hold a socket open. */
  timeoutMs?: number;
  probeUrl?: string;
  fetchImpl?: typeof fetch;
}

const DEFAULTS = {
  healthyIntervalMs: 60_000,
  offlineIntervalMs: 15_000,
  timeoutMs: 4_000,
  probeUrl: "/health/",
} as const;

export interface ProbeResult {
  reachable: boolean;
  /** Server time, used to estimate clock skew. POS terminals drift. */
  serverTime: string | null;
  roundTripMs: number;
}

/**
 * One reachability check.
 *
 * `cache: "no-store"` because a cached 200 from ten minutes ago is exactly the
 * wrong answer to "can I reach the server right now".
 */
export async function probe(options: ConnectivityOptions = {}): Promise<ProbeResult> {
  const { timeoutMs, probeUrl, fetchImpl } = { ...DEFAULTS, ...options };
  const doFetch = fetchImpl ?? globalThis.fetch;

  const started = Date.now();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await doFetch(probeUrl, {
      method: "GET",
      cache: "no-store",
      signal: controller.signal,
      headers: { Accept: "application/json" },
    });

    return {
      reachable: response.ok,
      serverTime: response.headers.get("Date"),
      roundTripMs: Date.now() - started,
    };
  } catch {
    // Any failure — abort, DNS, refused, CORS — means not reachable. The
    // distinction does not change what the cashier can do.
    return { reachable: false, serverTime: null, roundTripMs: Date.now() - started };
  } finally {
    clearTimeout(timer);
  }
}

type Listener = (reachable: boolean, result: ProbeResult) => void;

/**
 * Watches connectivity and notifies on change.
 *
 * Probes on an adaptive interval, and immediately on the browser's `online`
 * event and on tab focus — the two moments when a cashier is most likely to be
 * waiting to find out whether they can take a card again.
 */
export class ConnectivityMonitor {
  private readonly options: Required<Omit<ConnectivityOptions, "fetchImpl">> &
    Pick<ConnectivityOptions, "fetchImpl">;

  private listeners = new Set<Listener>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private started = false;

  /** null until the first probe completes — "checking", not "offline". */
  reachable: Reachability = null;

  constructor(options: ConnectivityOptions = {}) {
    this.options = { ...DEFAULTS, ...options };
  }

  start(): void {
    if (this.started) return;
    this.started = true;

    if (typeof window !== "undefined") {
      window.addEventListener("online", this.handleBrowserOnline);
      window.addEventListener("offline", this.handleBrowserOffline);
      document.addEventListener("visibilitychange", this.handleVisibility);
    }

    void this.check();
  }

  stop(): void {
    this.started = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;

    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.handleBrowserOnline);
      window.removeEventListener("offline", this.handleBrowserOffline);
      document.removeEventListener("visibilitychange", this.handleVisibility);
    }
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Probe now, reschedule, and notify if the answer changed. */
  async check(): Promise<ProbeResult> {
    // The browser flag is a fast negative: if the interface is down there is no
    // point spending four seconds discovering that.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      const result: ProbeResult = { reachable: false, serverTime: null, roundTripMs: 0 };
      this.settle(result);
      return result;
    }

    const result = await probe(this.options);
    this.settle(result);
    return result;
  }

  private settle(result: ProbeResult): void {
    const changed = this.reachable !== result.reachable;
    this.reachable = result.reachable;

    if (changed) {
      for (const listener of this.listeners) listener(result.reachable, result);
    }
    this.schedule();
  }

  private schedule(): void {
    if (!this.started) return;
    if (this.timer) clearTimeout(this.timer);

    const delay = this.reachable
      ? this.options.healthyIntervalMs
      : this.options.offlineIntervalMs;

    this.timer = setTimeout(() => void this.check(), delay);
  }

  private handleBrowserOnline = (): void => {
    void this.check();
  };

  private handleBrowserOffline = (): void => {
    this.settle({ reachable: false, serverTime: null, roundTripMs: 0 });
  };

  private handleVisibility = (): void => {
    if (document.visibilityState === "visible") void this.check();
  };
}

/**
 * Estimate the device's clock offset from the server.
 *
 * Half the round trip is subtracted as a crude allowance for latency. The
 * estimate is stored, never used to overwrite anything: reports use the corrected
 * value while the raw device timestamp is preserved for forensics. A terminal
 * with a wrong clock must never silently reassign a day's takings.
 */
export function estimateClockSkewMs(result: ProbeResult): number | null {
  if (!result.serverTime) return null;
  const serverMs = new Date(result.serverTime).getTime();
  if (Number.isNaN(serverMs)) return null;
  return serverMs - (Date.now() - result.roundTripMs / 2);
}
